import { ChevronDownIcon, CodeIcon, CopyIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import { Box, Collapse, Stack, alpha, useTheme } from '@mui/material';
import { useState } from 'react';
import { useToggle } from 'react-use';

const exampleConfigs = [
  {
    name: 'Playwright',
    config: `{
  "mcpServers": {
    "playwright": {
      "command": "npx",
      "args": ["@playwright/mcp@latest"]
    }
  }
}`,
  },
  {
    name: 'GitHub',
    config: `{
  "mcpServers": {
    "github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_TOKEN": "\${secrets.GITHUB_TOKEN}"
      }
    }
  }
}`,
  },
  {
    name: 'Filesystem',
    config: `{
  "mcpServers": {
    "filesystem": {
      "command": "npx",
      "args": [
        "-y",
        "@modelcontextprotocol/server-filesystem",
        "/path/to/allowed/dir"
      ]
    }
  }
}`,
  },
];

export const ConfigExamplesBar = ({
  onExampleClick,
}: {
  onExampleClick: (example: string) => void;
}) => {
  const { palette } = useTheme();

  const [open, toggleOpen] = useToggle(false);
  const [expandedExample, setExpandedExample] = useState<Set<string>>(new Set());

  return (
    <Stack>
      <Stack sx={{ mb: 1 }}>
        <Typography variant="body2">
          Find the MCP server configuration online and paste it here.
        </Typography>
        <Typography variant="caption2" sx={{ textWrap: 'wrap', color: palette.text.primary }}>
          Accepts CLI commands (Claude, Gemini, Codex), JSON server configs, and full mcpServers
          objects from GitHub READMEs
        </Typography>
      </Stack>
      <Box
        sx={{
          mb: 1.5,
          border: `1px solid ${palette.divider}`,
          borderRadius: 1.5,
          overflowY: 'hidden',
          backgroundColor: palette.background.paperCard,
        }}
      >
        <Group
          onClick={toggleOpen}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            cursor: 'pointer',
            userSelect: 'none',
            color: palette.foreground.paperCard.light,
            '&:hover': { backgroundColor: alpha(palette.text.primary, 0.04) },
          }}
        >
          <Group alignItems="center" gap={1}>
            <CodeIcon size={14} />
            <Typography
              variant="caption2"
              sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: '20px' }}
            >
              Complete Examples
            </Typography>
            <Typography variant="caption2" sx={{ fontSize: '0.7rem', lineHeight: '20px' }}>
              ({exampleConfigs.length}) — click to expand
            </Typography>
          </Group>
          <ChevronDownIcon
            size={14}
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </Group>

        <Collapse in={open}>
          <Stack spacing={1.5} sx={{ px: 1.5, pb: 1.5 }}>
            {exampleConfigs.map((example) => (
              <Box
                key={example.name}
                sx={{
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: `1px solid ${palette.divider}`,
                }}
              >
                <Group
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    bgcolor: palette.modal.background.card,
                  }}
                >
                  <Typography
                    variant="caption2"
                    sx={{ fontWeight: 600, color: palette.text.paper }}
                  >
                    {example.name}
                  </Typography>
                  <Group alignItems="center" gap={2}>
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() =>
                        setExpandedExample((prev) => {
                          if (prev.has(example.name)) {
                            return new Set([
                              ...Array.from(prev).filter((name) => name !== example.name),
                            ]);
                          }
                          return new Set([...Array.from(prev), example.name]);
                        })
                      }
                    >
                      {expandedExample.has(example.name) ? 'Hide config' : 'Show config'}
                    </Button>
                    <Tooltip title={`Use ${example.name} config`} arrow>
                      <IconButton
                        Icon={CopyIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onExampleClick(example.config);
                        }}
                      />
                    </Tooltip>
                  </Group>
                </Group>
                <Collapse in={expandedExample.has(example.name)}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      px: 1.5,
                      py: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      color: palette.text.paper,
                      bgcolor: palette.input.background.default,
                      borderTop: `1px solid ${palette.divider}`,
                    }}
                  >
                    {example.config}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );
};
